/*
 * Design tokens — My SCG.
 *
 * These are the hub's production tokens, copied verbatim from the My SCG design
 * kit so that a colleague moving between the two apps cannot tell where one ends
 * and the other begins. Do not introduce hues that are not in this block.
 *
 * Purple is primary (sidebar, primary actions, export headers). Lilac is accent
 * only — active nav, highlighted headings, badges, focus rings — never a large
 * fill, and --lilac-dark whenever lilac has to carry small text. Mint means
 * positive/available and nothing else. Depth comes from shadow, not borders.
 *
 * Light only, deliberately: the hub has no dark mode, and a dark variant of this
 * app would look like a different product rather than the same one.
 *
 * The series colours are NOT the brand colours. Purple and lilac sit too close
 * under protanopia to separate data, so the categorical set below is a
 * brand-anchored 8-slot palette run through the data-viz validator against both
 * the card (#fff) and the canvas (#f7f5fa): lightness band, chroma floor,
 * adjacent-pair CVD separation, normal-vision floor and contrast all pass.
 * Slot 1 is the brand purple so a single-series chart reads as "SCG"; the rest
 * step around the wheel far enough apart to stay legible at 26 consultants.
 * Fixed order, never cycled — a ninth series folds into "Other".
 */
:root {
  color-scheme: light;

  --purple: #3e2656;
  --purple-deep: #2c1a3e;
  --purple-soft: #4c3168;
  --lilac: #ac68bc;
  --lilac-soft: #f0eaf4;
  --lilac-dark: #8a4c9c;
  --mint: #25d5bf;

  --canvas: #f7f5fa;
  --card: #fff;
  --border: #ece7f2;
  --border-strong: #ddd3e6;
  --ink: #2b1c3a;
  --body: #5f5768;
  --muted: #8b8494;

  --ok: #0a5a4e;
  --okbg: #e2f6f1;
  --warn: #8a5a12;
  --warnbg: #fdf1df;
  --err: #a5322f;
  --errbg: #fbe9e9;
  --infobg: #efe6f4;
  --infotx: #6d4a7a;
  --approve: #17a98f;

  --radius: 16px;
  --radius-sm: 12px;
  --shadow-card: 0 2px 4px rgba(43, 28, 58, 0.05), 0 10px 30px rgba(43, 28, 58, 0.1);
  --shadow-card-hover: 0 3px 6px rgba(43, 28, 58, 0.06), 0 16px 40px rgba(43, 28, 58, 0.14);
  --shadow-frame: 0 2px 6px rgba(43, 28, 58, 0.06), 0 24px 64px rgba(43, 28, 58, 0.12);
  --ease: cubic-bezier(0.22, 1, 0.36, 1);

  /* Aliases kept so existing markup and any inline styles still resolve. */
  --brand: var(--purple);
  --brand-ink: #fff;
  --accent: var(--lilac);
  --surface: var(--card);
  --page: var(--canvas);
  --raised: #fff;
  --ink-2: var(--body);
  --grid: var(--border);
  --axis: #d8d0e2;
  --shadow: var(--shadow-card);
  --good: var(--approve);
  --warning: #d09a3c;
  --serious: #c8703f;
  --critical: var(--err);
  --good-text: var(--ok);

  --s1: #8b52b5;
  --s2: #b07407;
  --s3: #0d9488;
  --s4: #cf4436;
  --s5: #3b6fd4;
  --s6: #d1467a;
  --s7: #26734a;
  --s8: #5b5bb0;

  --font: 'Parkinsans', system-ui, -apple-system, 'Segoe UI', sans-serif;
}

* { box-sizing: border-box; }

html, body, #root { height: 100%; }

body {
  margin: 0;
  background: linear-gradient(160deg, #f1ecf5 0%, #eae4f0 55%, #e9e9f3 100%);
  color: var(--ink);
  font-family: var(--font);
  font-size: 14px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}

a { color: inherit; text-decoration: none; }
a:hover { text-decoration: none; }

/* Lilac at 2px — the accent doing the work it is allowed to do. */
:focus-visible { outline: 2px solid var(--lilac); outline-offset: 2px; border-radius: 8px; }

/* ------------------------------------------------------------------ shell -- */

/* The whole app lives in a centred floating frame, exactly as the hub does. It
   is the single most recognisable thing about the My SCG layout — a full-bleed
   app reads as a different product no matter how well the colours match. */
.shell {
  display: flex;
  max-width: 1280px;
  margin: 26px auto;
  background: var(--canvas);
  border: 1px solid rgba(228, 223, 234, 0.9);
  border-radius: 22px;
  overflow: hidden;
  min-height: calc(100vh - 52px);
  box-shadow: var(--shadow-frame);
}

.sidebar {
  width: 212px;
  flex: 0 0 auto;
  position: relative;
  background: linear-gradient(178deg, var(--purple) 0%, var(--purple-deep) 100%);
  color: #fff;
  padding: 22px 0 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
/* The triangle motif, echoing the corner triangles on spencerclarkegroup.co.uk.
   Pure clip-path so there is no asset to ship or scale. */
.sidebar::after {
  content: '';
  position: absolute; right: -46px; bottom: -46px; width: 150px; height: 150px;
  background: linear-gradient(135deg, rgba(172, 104, 188, 0.35), rgba(37, 213, 191, 0.18));
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
  pointer-events: none;
}
.sidebar::before {
  content: '';
  position: absolute; right: 18px; bottom: 66px; width: 44px; height: 44px;
  background: rgba(172, 104, 188, 0.16);
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
  pointer-events: none;
}

/* The logo alone, per the hub — no wordmark text beside or beneath it. */
.sidebar .wordmark { padding: 0 20px 18px; position: relative; z-index: 1; }
.sidebar .wordmark img { display: block; width: 148px; height: auto; }
/* Shown only if the asset is missing — never both at once. */
.sidebar .wordmark .fallback { font-size: 16px; font-weight: 700; letter-spacing: -0.01em; color: #fff; }

.sidebar nav { display: flex; flex-direction: column; gap: 2px; position: relative; z-index: 1; }
/* Sentence case, like everything else — table headers are the only ALL CAPS. */
.sidebar .nav-group {
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.01em;
  color: rgba(255, 255, 255, 0.42); margin: 16px 0 5px; padding: 0 20px;
}
.sidebar a.nav-item {
  display: flex; align-items: center; gap: 9px;
  padding: 10px 15px; margin: 0 12px; border-radius: 12px;
  color: rgba(255, 255, 255, 0.82); font-size: 12.5px; font-weight: 500; cursor: pointer;
  transition: background 0.15s var(--ease), color 0.15s var(--ease), transform 0.15s var(--ease);
}
.sidebar a.nav-item:hover { background: rgba(172, 104, 188, 0.22); color: #fff; transform: translateX(2px); }
.sidebar a.nav-item.active {
  background: linear-gradient(120deg, var(--lilac), #9a58aa);
  color: #fff;
  box-shadow: 0 4px 14px rgba(172, 104, 188, 0.35);
}

.sidebar .spacer { flex: 1; }
.sidebar .who {
  font-size: 12px; margin: 0 20px; padding-top: 14px; position: relative; z-index: 1;
  border-top: 1px solid rgba(172, 104, 188, 0.35);
}
.sidebar .who b { display: block; font-weight: 600; }
.sidebar .who span { opacity: 0.68; font-size: 11px; }
.sidebar .who .role {
  display: inline-block; margin-top: 7px; font-size: 10px; font-weight: 600;
  letter-spacing: 0.06em; background: rgba(172, 104, 188, 0.3);
  padding: 3px 9px; border-radius: 999px;
}
.sidebar .back-link {
  display: block; margin: 10px 20px 0; font-size: 11px;
  color: rgba(255, 255, 255, 0.62); position: relative; z-index: 1;
  transition: color 0.15s var(--ease);
}
.sidebar .back-link:hover { color: #fff; }

.main { flex: 1; min-width: 0; padding: 24px 28px 34px; }

/* Greeting in lilac above the title in ink — the hub's signature top bar. */
.topbar {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; flex-wrap: wrap; margin-bottom: 18px;
}
.topbar .greet { font-size: 24px; font-weight: 600; letter-spacing: -0.01em; color: var(--lilac); }

.page-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 20px; margin-bottom: 20px; flex-wrap: wrap;
}
.page-head .greet { font-size: 24px; font-weight: 600; letter-spacing: -0.01em; color: var(--lilac); }
.page-head h1 { margin: 2px 0 0; font-size: 26px; font-weight: 700; letter-spacing: -0.015em; }
.page-head p { margin: 6px 0 0; color: var(--body); font-size: 12.5px; max-width: 70ch; line-height: 1.6; }
.page-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

.avatar-chip { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 500; }
.avatar-circle {
  width: 34px; height: 34px; border-radius: 50%; background: #ead9f0; color: var(--purple);
  font-size: 12px; font-weight: 600; display: flex; align-items: center; justify-content: center;
  position: relative; flex: none;
}
.avatar-circle::after {
  content: ''; position: absolute; bottom: 1px; right: 1px; width: 9px; height: 9px;
  border-radius: 50%; background: var(--mint); border: 2px solid var(--canvas);
}

/* ---------------------------------------------------------------- controls -- */

button, .btn {
  font: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer;
  border-radius: var(--radius-sm); padding: 9px 15px;
  border: 1.5px solid var(--border); background: #fff; color: var(--body);
  transition: border-color 0.15s var(--ease), color 0.15s var(--ease), transform 0.15s var(--ease),
    box-shadow 0.15s var(--ease);
}
button:hover:not(:disabled), .btn:hover { border-color: var(--lilac); color: var(--ink); transform: translateY(-1px); }
button:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

button.primary {
  background: linear-gradient(120deg, var(--purple), var(--purple-soft));
  border-color: transparent; color: #fff; font-weight: 600; font-size: 13px; padding: 11px 18px;
  box-shadow: 0 3px 10px rgba(62, 38, 86, 0.25);
}
button.primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(62, 38, 86, 0.32); }
button.primary:active { transform: translateY(0); }

button.danger { color: var(--err); border-color: rgba(165, 50, 47, 0.35); }
button.danger:hover:not(:disabled) { background: var(--errbg); border-color: var(--err); color: var(--err); }

button.ghost { background: transparent; border-color: transparent; color: var(--body); padding: 6px 10px; }
button.ghost:hover:not(:disabled) { background: rgba(172, 104, 188, 0.12); color: var(--ink); transform: none; }

button.small { font-size: 11.5px; padding: 6px 12px; }

input, select, textarea {
  font: inherit; font-size: 13.5px; width: 100%; padding: 10px 13px;
  border: 1.5px solid var(--border); border-radius: 11px;
  background: #fbfafd; color: var(--ink);
  transition: border-color 0.15s var(--ease), background 0.15s var(--ease), box-shadow 0.15s var(--ease);
}
input:hover, select:hover, textarea:hover { border-color: var(--border-strong); }
input:focus, select:focus, textarea:focus {
  outline: none; border-color: var(--lilac); background: #fff;
  box-shadow: 0 0 0 3px rgba(172, 104, 188, 0.16);
}
textarea { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 12px; line-height: 1.6; }
input[type='checkbox'] { width: auto; accent-color: var(--lilac-dark); }

label.field { display: block; margin-bottom: 14px; }
label.field > span.label { display: block; font-size: 11.5px; font-weight: 600; color: var(--body); margin-bottom: 5px; }
/* The app explains itself in whispers — rules live under the thing they govern. */
label.field > span.help { display: block; font-size: 11px; color: var(--muted); margin-top: 5px; line-height: 1.55; }

.row { display: flex; gap: 12px; flex-wrap: wrap; }
.row > * { flex: 1 1 180px; min-width: 0; }

.filter-bar {
  display: flex; gap: 12px 14px; align-items: flex-start; flex-wrap: wrap;
  background: var(--card); border: 1px solid var(--border); border-radius: var(--radius);
  box-shadow: var(--shadow-card); padding: 18px 20px; margin-bottom: 16px;
}
.filter-bar label.field { margin-bottom: 0; min-width: 160px; }
.filter-bar > .filter-actions { margin-top: 22px; display: flex; gap: 10px; flex-wrap: wrap; }

/* Pill tabs — the hub's section switcher. */
.tabs {
  display: flex; gap: 4px; background: rgba(255, 255, 255, 0.75);
  border: 1px solid var(--border); border-radius: 999px; padding: 4px;
  width: max-content; max-width: 100%; overflow-x: auto; margin-bottom: 16px;
}
.tabs button {
  border: none; background: none; border-radius: 999px; padding: 7px 15px;
  color: var(--muted); font-size: 12px; font-weight: 500; white-space: nowrap;
}
.tabs button:hover { color: var(--ink); transform: none; border-color: transparent; }
.tabs button.active {
  background: linear-gradient(120deg, var(--lilac), #9a58aa); color: #fff;
  box-shadow: 0 3px 10px rgba(172, 104, 188, 0.3);
}

/* ------------------------------------------------------------------ cards -- */

.card {
  background: var(--card); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 20px; box-shadow: var(--shadow-card);
  transition: box-shadow 0.2s var(--ease);
}
.card:hover { box-shadow: var(--shadow-card-hover); }
.card + .card { margin-top: 14px; }
.card h2, .card h3 { margin: 0 0 12px; font-size: 14px; font-weight: 600; letter-spacing: -0.005em; }
.card .note { font-size: 11px; color: var(--muted); margin: 10px 0 0; line-height: 1.55; }

.grid-cards { display: grid; gap: 14px; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); }

.report-card { display: flex; flex-direction: column; gap: 8px; cursor: pointer; text-align: left; }
.report-card:hover { box-shadow: var(--shadow-card-hover); }
.report-card .cat { font-size: 11px; color: var(--lilac-dark); font-weight: 600; }
.report-card h3 { margin: 0; font-size: 15px; font-weight: 600; }
.report-card p { margin: 0; font-size: 12.5px; color: var(--body); line-height: 1.6; }
.report-card .foot { font-size: 11px; color: var(--muted); margin-top: 4px; }

/* -------------------------------------------------------------- stat tiles -- */

.kpi-row { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(148px, 1fr)); }
.kpi {
  background: var(--card); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 16px 18px; box-shadow: var(--shadow-card);
  transition: box-shadow 0.2s var(--ease);
}
.kpi:hover { box-shadow: var(--shadow-card-hover); }
.kpi .label { font-size: 11.5px; color: var(--body); margin-bottom: 6px; }
/* Proportional figures on standalone numbers; tabular-nums only in columns. */
.kpi .value { font-size: 27px; font-weight: 700; letter-spacing: -0.022em; line-height: 1.08; color: var(--ink); }
.kpi .sub { font-size: 11px; color: var(--muted); margin-top: 4px; }

/* ------------------------------------------------------------------ table -- */

.table-wrap { overflow-x: auto; }

table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
/* The only ALL CAPS anywhere in the product. */
th {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--muted);
  font-weight: 600; text-align: left; padding: 8px 10px; border-bottom: 1.5px solid var(--border);
  white-space: nowrap;
}
td { padding: 11px 10px; border-bottom: 1px solid #f2eef6; vertical-align: middle; }
tbody tr { transition: background 0.12s var(--ease); }
tbody tr:hover { background: #faf8fc; }
tbody tr:last-child td { border-bottom: none; }
td.right, th.right { text-align: right; font-variant-numeric: tabular-nums; }
.mono { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 11.5px; }
.small { font-size: 11px; color: var(--muted); }
.note { font-size: 11px; color: var(--muted); line-height: 1.55; }
.sub { font-size: 12.5px; color: var(--body); line-height: 1.6; }

/* Movement is never colour alone — the arrow carries it too. */
.delta.up { color: var(--ok); }
.delta.down { color: var(--err); }

/* ----------------------------------------------------------------- charts -- */

.legend { display: flex; flex-wrap: wrap; gap: 6px 16px; margin-bottom: 10px; font-size: 11.5px; color: var(--body); }
.legend-item { display: inline-flex; align-items: center; gap: 6px; }
.legend-item i { width: 10px; height: 10px; border-radius: 3px; }
.chart-shell { position: relative; }

.tooltip {
  position: absolute; pointer-events: none; z-index: 5;
  background: var(--purple-deep); color: #fff; border-radius: var(--radius-sm);
  padding: 9px 12px; font-size: 11.5px; box-shadow: 0 10px 30px rgba(20, 10, 30, 0.35);
  min-width: 140px;
}
.tooltip .t-title { font-weight: 600; margin-bottom: 5px; }
.tooltip .t-row { display: flex; align-items: center; gap: 7px; justify-content: space-between; }
.tooltip .t-row i { width: 9px; height: 9px; border-radius: 2px; flex: none; }
.tooltip .t-row b { font-variant-numeric: tabular-nums; }
.toggle-table { margin-top: 12px; }

.funnel-row {
  display: grid; grid-template-columns: minmax(120px, 170px) 1fr 64px minmax(120px, 170px);
  gap: 12px; align-items: center; padding: 5px 0;
}
.funnel-label { font-size: 12.5px; color: var(--body); }
.funnel-track { background: var(--lilac-soft); border-radius: 5px; height: 18px; overflow: hidden; }
.funnel-fill { height: 100%; border-radius: 5px; }
.funnel-value { text-align: right; font-weight: 600; font-variant-numeric: tabular-nums; }
.funnel-rate { font-size: 11.5px; color: var(--body); font-variant-numeric: tabular-nums; }

/* --------------------------------------------------------------- messaging -- */

/* Soft rounded banners, plain words, no codes. */
.banner {
  border-radius: var(--radius-sm); padding: 11px 14px; font-size: 12.5px; line-height: 1.55;
  margin-bottom: 14px; display: flex; gap: 9px; align-items: flex-start;
}
.banner .icon { font-weight: 700; flex: none; }
.banner ul { margin: 6px 0 0; padding-left: 18px; }
.banner.warn { background: var(--warnbg); color: var(--warn); }
.banner.error { background: var(--errbg); color: var(--err); }
.banner.info { background: var(--infobg); color: var(--infotx); }
.banner.good { background: var(--okbg); color: var(--ok); }

/* State is a chip, never coloured text alone. */
.pill {
  display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600;
  border-radius: 999px; padding: 5px 11px; white-space: nowrap;
  background: var(--lilac-soft); color: var(--lilac-dark);
}
.pill.ok { background: var(--okbg); color: var(--ok); }
.pill.bad { background: var(--errbg); color: var(--err); }
.pill.warn { background: var(--warnbg); color: var(--warn); }
.pill.off { background: #f3f0f7; color: var(--muted); }

.badge {
  background: var(--lilac); color: #fff; border-radius: 999px;
  font-size: 10.5px; padding: 2px 8px; font-weight: 600;
}

/* Empty states explain themselves and say when the thing arrives. */
.empty-state {
  text-align: center; padding: 38px 24px; color: var(--body); font-size: 13px; line-height: 1.6;
  border: 1.5px dashed #ddd0e6; border-radius: var(--radius); background: rgba(255, 255, 255, 0.7);
}
.empty-state h3 { color: var(--ink); font-size: 14px; font-weight: 600; margin: 0 0 6px; }
.empty-state p { margin: 0 auto; max-width: 46ch; }

/* Refetch holds the previous render at reduced opacity — no skeleton flash. */
.stale { opacity: 0.55; transition: opacity 0.15s var(--ease); }

.spinner {
  width: 15px; height: 15px; border: 2px solid var(--border);
  border-top-color: var(--lilac); border-radius: 50%; display: inline-block;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ------------------------------------------------------------------ login -- */

/* The purple stage, with the triangle motif at both corners. */
.login-wrap {
  display: grid; place-items: center; min-height: 100vh; padding: 24px;
  background: linear-gradient(150deg, var(--purple) 0%, var(--purple-deep) 70%, #241533 100%);
  position: relative; overflow: hidden;
}
.login-wrap::before {
  content: ''; position: absolute; left: -120px; bottom: -120px; width: 380px; height: 380px;
  background: linear-gradient(135deg, rgba(172, 104, 188, 0.28), rgba(37, 213, 191, 0.12));
  clip-path: polygon(0 0, 0 100%, 100% 100%); pointer-events: none;
}
.login-wrap::after {
  content: ''; position: absolute; right: -80px; top: -80px; width: 260px; height: 260px;
  background: rgba(172, 104, 188, 0.14);
  clip-path: polygon(100% 0, 100% 100%, 0 0); pointer-events: none;
}
.login-card {
  width: 100%; max-width: 400px; background: #fff; border-radius: 20px; overflow: hidden;
  box-shadow: 0 24px 64px rgba(20, 10, 30, 0.4); position: relative; z-index: 1;
}
.login-card .head { padding: 28px 30px 0; }
.login-card .head img { display: block; width: 172px; height: auto; margin-bottom: 18px; }
.login-card .head .org { font-size: 11px; color: var(--lilac-dark); font-weight: 500; }
.login-card .head h1 { margin: 4px 0 0; font-size: 20px; font-weight: 700; color: var(--ink); }
.login-card .body { padding: 20px 30px 30px; }

/* ------------------------------------------------------------------- misc -- */

.split { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; }
@media (max-width: 900px) { .split { grid-template-columns: minmax(0, 1fr); } }

.dl {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 14px 24px; margin: 0;
}
.dl dt { font-size: 11px; color: var(--muted); font-weight: 600; }
.dl dd { margin: 3px 0 0; font-size: 13.5px; font-weight: 500; color: var(--ink); }

.raw {
  background: var(--purple-deep); color: #e8ddf0; border-radius: var(--radius-sm);
  padding: 14px 16px; font-family: ui-monospace, Menlo, monospace; font-size: 11px;
  line-height: 1.7; overflow-x: auto; white-space: pre;
}
code { background: var(--canvas); border-radius: 8px; padding: 3px 7px; font-size: 11.5px; }

.tick { color: var(--approve); font-weight: 700; }

/* Preview banner — standalone single-file build only. Deliberately loud: nobody
   should mistake a captured preview for the live platform. */
.preview-banner {
  background: var(--warnbg); color: var(--warn); font-size: 12px;
  padding: 11px 18px; text-align: center; line-height: 1.5;
}
.preview-banner strong { font-weight: 700; }
.preview-banner + .shell { min-height: calc(100vh - 96px); margin-top: 14px; }

/* ---------------------------------------------------------- director view -- */
/* The reports-only screen for Mike and Thomas. Same tokens, same frame; larger
   targets and fewer of them. Lilac stays an accent: the selected period chip
   and focus rings, never a fill behind content. */

.director-hero { margin: 6px 0 24px; }
.director-hero .greet { font-size: 22px; font-weight: 600; letter-spacing: -0.01em; color: var(--lilac); }
.director-hero h1 { margin: 4px 0 0; font-size: 28px; font-weight: 700; letter-spacing: -0.02em; }
.director-hero p { margin: 6px 0 0; color: var(--body); font-size: 13px; max-width: 60ch; }
.director-search { margin-top: 16px; max-width: 420px; background: #fff; }

.director-cat { font-size: 12.5px; font-weight: 600; color: var(--lilac-dark); margin: 0 0 10px; }
.director-tiles { display: grid; gap: 14px; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
.director-tile {
  display: flex; flex-direction: column; align-items: flex-start; gap: 6px; text-align: left;
  min-height: 132px; padding: 20px 20px 16px; color: var(--ink); margin: 0;
}
.director-tile:hover:not(:disabled) { color: var(--ink); border-color: var(--border); }
/* .card + .card adds a top margin meant for stacked cards; in a grid it
   pushes every tile after the first down out of line. */
.director-tiles > .card { margin-top: 0; }
.director-tile .tile-name { font-size: 16px; font-weight: 600; letter-spacing: -0.01em; }
.director-tile .tile-desc { font-size: 12.5px; color: var(--body); line-height: 1.55; }
.director-tile .tile-go { margin-top: auto; padding-top: 8px; font-size: 12px; font-weight: 600; color: var(--lilac-dark); }

.director-recent {
  display: flex; justify-content: space-between; align-items: center; gap: 12px; width: 100%;
  border: none; border-radius: 0; background: transparent; padding: 11px 20px; text-align: left; color: var(--ink);
}
.director-recent + .director-recent { border-top: 1px solid #f2eef6; }
.director-recent:hover:not(:disabled) { background: #faf8fc; transform: none; }

.director-preview {
  display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;
  background: var(--infobg); color: var(--infotx); border-radius: var(--radius-sm);
  padding: 10px 14px; margin-bottom: 18px; font-size: 12.5px;
}
.director-preview button.small { background: #fff; }

.director-back { padding-left: 0; color: var(--lilac-dark); font-weight: 600; }

.director-periods { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin: 0 0 14px; }
.chip {
  border-radius: 999px; padding: 8px 15px; font-size: 12.5px; font-weight: 500;
  background: #fff; border: 1.5px solid var(--border); color: var(--body);
}
.chip.on {
  border-color: var(--lilac); color: var(--purple); font-weight: 600;
  box-shadow: 0 0 0 3px rgba(172, 104, 188, 0.14);
}
.custom-range { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.custom-range input { width: auto; padding: 7px 10px; font-size: 12.5px; }

.director-actions {
  display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap;
  padding: 14px 18px; margin-bottom: 16px;
}
.director-actions .as-at { display: flex; flex-direction: column; line-height: 1.4; }
.director-actions .as-at strong { font-size: 15px; font-weight: 600; }
.director-actions .as-at span { font-size: 11.5px; color: var(--muted); }
.director-actions .buttons { display: flex; gap: 8px; flex-wrap: wrap; }
.btn { display: inline-flex; align-items: center; }
.btn.primary-link {
  background: linear-gradient(120deg, var(--purple), var(--purple-soft));
  border-color: transparent; color: #fff; font-weight: 600;
  box-shadow: 0 3px 10px rgba(62, 38, 86, 0.25);
}
.btn.primary-link:hover { color: #fff; box-shadow: 0 6px 16px rgba(62, 38, 86, 0.32); }

.director-problem h3 { margin: 0 0 6px; }
.director-problem p { margin: 0 0 14px; color: var(--body); font-size: 13px; max-width: 64ch; }

.about-figures { margin-top: 16px; font-size: 12.5px; color: var(--body); }
.about-figures > summary { cursor: pointer; font-weight: 600; color: var(--ink); }
.about-figures ul { margin: 10px 0 0; padding-left: 18px; line-height: 1.6; }
.about-figures li + li { margin-top: 4px; }

@media (max-width: 640px) {
  .shell.director { flex-direction: column; margin: 0; border-radius: 0; border: none; min-height: 100vh; }
  .shell.director .sidebar { width: auto; flex-direction: row; flex-wrap: wrap; align-items: center; padding: 12px 8px; gap: 6px; }
  .shell.director .sidebar .wordmark { padding: 0 8px; }
  .shell.director .sidebar .wordmark img { width: 104px; }
  .shell.director .sidebar nav { flex-direction: row; }
  .shell.director .sidebar a.nav-item { margin: 0; padding: 7px 11px; }
  .shell.director .sidebar .spacer, .shell.director .sidebar .who span,
  .shell.director .sidebar .back-link, .shell.director .sidebar::before { display: none; }
  .shell.director .sidebar .who { border: none; margin: 0 0 0 auto; padding: 0; }
  .shell.director .sidebar .who b { display: none; }
  .director-hero h1 { font-size: 22px; }
  .director-actions .buttons { width: 100%; }
}

/* ------------------------------------------------------ responsive & motion -- */

@media (max-width: 900px) {
  .shell { margin: 10px 8px; border-radius: 16px; min-height: calc(100vh - 20px); }
  .sidebar { width: 168px; }
  .main { padding: 18px 16px 26px; }
  .page-head h1 { font-size: 22px; }
  .page-head .greet { font-size: 20px; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { transition: none !important; animation: none !important; }
}

@media print {
  body { background: #fff; }
  .shell { box-shadow: none; border: none; margin: 0; max-width: none; }
  .sidebar, .page-actions, .director-periods, .director-actions .buttons, .director-back { display: none; }
  .card, .kpi { box-shadow: none; break-inside: avoid; }
}
